import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Linkedin, Mail, Download } from "lucide-react";
import { BRAND, SERVICES, OFFICES } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      toast.error("Enter a valid email");
      return;
    }
    try {
      setLoading(true);
      await axios.post(`${API}/newsletter`, { email });
      toast.success("Subscribed. Welcome to the Notion Minds brief.");
      setEmail("");
    } catch {
      toast.error("Could not subscribe. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer
      data-testid="site-footer"
      className="relative border-t border-white/10 bg-[#050505] mt-24"
    >
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12 pt-20 pb-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <div className="flex flex-col gap-4 mb-4">
              <img
                src={BRAND.logo}
                alt={BRAND.full}
                style={{
                  mixBlendMode: "screen",
                  filter: "brightness(1.55) saturate(1.35) contrast(1.08) drop-shadow(0 0 12px rgba(238,106,45,0.3))",
                }}
                className="h-28 w-auto object-contain"
              />
              <img
                src={BRAND.logoArabic}
                alt={BRAND.arabic}
                style={{
                  mixBlendMode: "screen",
                  filter: "brightness(1.55) saturate(1.35) contrast(1.08) drop-shadow(0 0 12px rgba(91,74,200,0.3))",
                }}
                className="h-24 w-auto object-contain"
              />
            </div>
            <p className="text-white/60 text-base leading-relaxed max-w-md">
              {BRAND.tagline} Strategy, security and delivery for ambitious
              enterprises in Saudi Arabia, Oman and Qatar.
            </p>

            <form onSubmit={subscribe} className="mt-8 max-w-md" data-testid="newsletter-form">
              <p className="font-mono-accent text-xs text-white/50 mb-3">
                The Notion Minds Brief — monthly
              </p>
              <div className="flex items-stretch border border-white/15 focus-within:border-[#d4af37] transition-colors">
                <input
                  data-testid="newsletter-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@enterprise.com"
                  className="flex-1 bg-transparent px-4 py-3 text-sm outline-none placeholder:text-white/30"
                />
                <button
                  data-testid="newsletter-subscribe-button"
                  type="submit"
                  disabled={loading}
                  className="px-5 bg-[#d4af37] text-black text-sm font-semibold hover:bg-[#e5c059] transition-colors disabled:opacity-60"
                >
                  {loading ? "..." : "Subscribe"}
                </button>
              </div>
            </form>

            <a
              href={`${API}/presentation/download`}
              data-testid="footer-deck-download"
              className="mt-6 inline-flex items-center gap-2 text-sm text-white/70 hover:text-[#d4af37] border-b border-white/15 hover:border-[#d4af37] pb-1 transition-colors"
            >
              <Download className="w-4 h-4" />
              Download corporate deck (.pptx)
            </a>
          </div>

          <div className="md:col-span-3">
            <p className="font-mono-accent text-xs text-white/50 mb-4">Services</p>
            <ul className="space-y-3">
              {SERVICES.map((s) => (
                <li key={s.id}>
                  <Link
                    to="/services"
                    data-testid={`footer-service-${s.id}`}
                    className="text-white/70 hover:text-[#d4af37] text-sm transition-colors"
                  >
                    {s.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-4">
            <p className="font-mono-accent text-xs text-white/50 mb-4">Regional Offices</p>
            <ul className="space-y-5">
              {OFFICES.map((o) => (
                <li key={o.city} className="text-sm">
                  <div className="text-white font-display text-base">
                    {o.city}, <span className="text-white/60">{o.country}</span>
                  </div>
                  <div className="text-white/50 text-xs mt-1">{o.address}</div>
                  {o.address2 && (
                    <div className="text-white/50 text-xs">{o.address2}</div>
                  )}
                  {o.zip && (
                    <div className="text-white/50 text-xs">ZIP {o.zip}</div>
                  )}
                  <div className="text-white/50 text-xs mt-1">{o.phone} · {o.email}</div>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <p className="text-white/40 text-xs font-mono-accent">
            © {new Date().getFullYear()} {BRAND.full}. All rights reserved.
          </p>
          <div className="flex items-center gap-4">
            <a
              href="https://www.linkedin.com"
              target="_blank"
              rel="noreferrer"
              data-testid="footer-linkedin"
              className="w-9 h-9 border border-white/15 flex items-center justify-center text-white/60 hover:border-[#d4af37] hover:text-[#d4af37] transition-colors"
            >
              <Linkedin className="w-4 h-4" />
            </a>
            <a
              href="mailto:hello@notionminds.sa"
              data-testid="footer-mail"
              className="w-9 h-9 border border-white/15 flex items-center justify-center text-white/60 hover:border-[#d4af37] hover:text-[#d4af37] transition-colors"
            >
              <Mail className="w-4 h-4" />
            </a>
            <Link
              to="/contact"
              data-testid="footer-contact-cta"
              className="inline-flex items-center gap-2 text-sm text-white hover:text-[#d4af37] transition-colors"
            >
              Start a conversation <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
